import React from "react";
import { Grid } from "@mui/material";
import TopicLimitedTextField from "../../common/topicLimitedTextField";
import { DIALOG_BODY_PADDING, MAX_TOPIC_NAME_LENGTH } from "modules/topics/styles/constants";
import { BasicTopicGroup, TopicDetails, TopicGroup, TopicToggleStatus } from "modules/topics/models";
import MandatoryField from "../../common/mandatoryField";
import TopicGroupSearch from "./topicGroupSearch";
import TopicDialogToggles from "./permissions/topicDialogToggles";
import { useDispatch, useSelector } from "react-redux";
import { GlobalApplicationState } from "globalApplicationState";
import { topicSlice } from "modules/topics/reducer";

interface ComponentProps {
    topicGroups: TopicGroup[];
    input: TopicDetails;
    setInput: (input: TopicDetails) => void;
    setIsError: (isError: boolean) => void;
    defaultGroup?: BasicTopicGroup;
}

const TopicCreationBody: React.FunctionComponent<ComponentProps> = (props) => {
    const {
        errorMessage
    } = useSelector((state: GlobalApplicationState) => state.topics);
    const dispatch = useDispatch();
    const [isToggleError, setToggleError] = React.useState(false);

    const onChangeName = (name: string) => {
        dispatch(topicSlice.actions.SET_ERR_MSG(""));
        if (props.defaultGroup !== null && props.defaultGroup !== undefined) {
            props.setInput({ ...props.input, name, groupId: props.defaultGroup.id });
        }
        else {
            props.setInput({ ...props.input, name });
        }
    };

    React.useEffect(() => {
        props.setIsError(isToggleError || errorMessage !== "")
    }, [isToggleError, errorMessage])

    const getNameField = () => {
        return <TopicLimitedTextField
            value={props.input.name}
            maxLength={MAX_TOPIC_NAME_LENGTH}
            onChange={onChangeName}
            errorText={errorMessage}/>;
    };

    const onSelectGroup = (groupId?: string) => props.setInput({ ...props.input, groupId: groupId ?? "" });

    const getGroupField = () : JSX.Element => {
        return <TopicGroupSearch
            topicGroups={
                props.topicGroups.map(tg => ({ label: tg.name, id: tg.id }))
            }
            onSelect={onSelectGroup}
            defaultGroup={props.defaultGroup}
        />;
    };

    const onChangeAudiences = (updatedFilters: Partial<TopicDetails>) =>
        props.setInput({ ...props.input, ...updatedFilters });

    const getTogglesStatus = () : TopicToggleStatus => ({
        isMandatory: props.input.mandatory,
        isDefault: props.input.default,
        isRestricted: props.input.restricted
    });

    const getSelectedAudiences = () => ({
        mandatory: props.input.mandatoryFor,
        default: props.input.defaultFor,
        restricted: props.input.restrictedTo
    });

    return (
        <React.Fragment>
            <Grid container
                alignItems={"center"}
                paddingTop={DIALOG_BODY_PADDING}
                paddingBottom={DIALOG_BODY_PADDING}
                rowSpacing={DIALOG_BODY_PADDING}
            >
                <MandatoryField
                    fieldName="Topic name"
                    fieldInput={getNameField()}
                    fieldStyle={{marginTop: "20px"}}/>

                <MandatoryField
                    fieldName="Topic group"
                    fieldInput={getGroupField()}/>

            </Grid>

            <Grid container
                alignItems={"baseline"}
                paddingTop={DIALOG_BODY_PADDING}
                paddingBottom={DIALOG_BODY_PADDING}
                rowSpacing={DIALOG_BODY_PADDING}
            >
                <TopicDialogToggles
                    prevAudiences={getSelectedAudiences()}
                    toggleStatus={getTogglesStatus()}
                    setIsError={setToggleError}
                    setInput={onChangeAudiences} />
            </Grid>
        </React.Fragment>
    );
};

export default TopicCreationBody;